import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Doc, ListMethodArg, ListMethodResponse, GetMethodResponse, ModelClass, References } from '/imports/types';
import { MUsers } from '/imports/api/musers';

// One record per blacklisted muser. `_id` is the lowercased username (natural
// dedupe + O(1) case-insensitive lookup); `username` keeps the display case.
// Bots abort accepting a trade from any partner whose name is in here
// (see server/mogbot/bot/bot.ts acceptTrade).
export interface Blacklist extends Doc {
  _id: string;
  username: string;
}

/** Canonical _id for a username (case-insensitive identity). */
export const blacklistKey = (username: string) => username.trim().toLowerCase();

type Model = Blacklist;
type Refs = References<Model>;

export const Blacklists = new Mongo.Collection<Blacklist>('blacklists');

export const listBlacklists = DeclareMethod<ListMethodArg<Blacklist>, ListMethodResponse<Blacklist, Refs>>('listBlacklists');
export const getBlacklist = DeclareMethod<string, GetMethodResponse<Blacklist, Refs>>('getBlacklist');
export const createBlacklist = DeclareMethod<Partial<Blacklist>, string>('createBlacklist');
export const updateBlacklist = DeclareMethod<Partial<Blacklist>, string>('updateBlacklist');
export const deleteBlacklist = DeclareMethod<string, undefined>('deleteBlacklist');

// Friendly blacklist-by-username API (avoids hand-editing records).
export const blacklistMuser = DeclareMethod<{ username: string }, Blacklist[]>('blacklistMuser');
export const unblacklistMuser = DeclareMethod<{ username: string }, Blacklist[]>('unblacklistMuser');
export const listBlacklistedMusers = DeclareMethod<void, Blacklist[]>('listBlacklistedMusers');

export const modelClass: ModelClass<Model, Refs> = {
  collection: Blacklists,
  editableFields: ['username'],
  searchQuery: (search?: string) => {
    search = search?.trim();
    return search?.length ? { username: new RegExp(search, 'i') } as any : {};
  },
  listMethod: listBlacklists,
  getMethod: getBlacklist,
  createMethod: createBlacklist,
  updateMethod: updateBlacklist,
  deleteMethod: deleteBlacklist,
  pathPrefix: '/blacklists',
  modelName: 'Blacklist',
  pluralName: 'Blacklists',
};

// Migrate the legacy singleton `{ ids: number[] }` doc to one record per
// username: resolve each uid to its muser name, insert {_id, username}, then
// drop the legacy doc. Idempotent — only runs while a legacy doc exists.
Meteor.startup(async () => {
  if (Meteor.isClient) return;
  if (process.env.THREAD) return;
  const legacy = await Blacklists.find({ ids: { $exists: true } } as any).fetchAsync() as any[];
  for (const doc of legacy) {
    const ids: number[] = Array.isArray(doc.ids) ? doc.ids : [];
    if (ids.length) {
      const musers = await MUsers.find({ _id: { $in: ids } }).fetchAsync();
      const nameByUid = new Map(musers.map((m) => [m._id, m.name]));
      for (const uid of ids) {
        const name = nameByUid.get(uid);
        if (!name) continue; // can't migrate a uid with no known username; skipped
        await Blacklists.upsertAsync(blacklistKey(name), { $set: { username: name } });
      }
    }
    await Blacklists.removeAsync(doc._id);
  }
});
